// =====================================================
// COUNTERFACTUAL PROOF ENGINE - SERVICE
// Tracks price movements after NO_TRADE/WAIT decisions
// to quantify "Capital Preserved"
// =====================================================

import { supabase } from '@/integrations/supabase/client';
import type { 
  CounterfactualTracking, 
  CounterfactualTrackingRow, 
  CounterfactualStats,
  CreateCounterfactualInput 
} from '@/types/counterfactual';
import { rowToCounterfactual } from '@/types/counterfactual';

// Default risk amount per trade (can be customized per user later)
const DEFAULT_RISK_AMOUNT_USD = 100;

/**
 * Create a counterfactual tracking entry for a NO_TRADE/WAIT decision
 */
export async function createCounterfactualTracking(
  input: CreateCounterfactualInput
): Promise<CounterfactualTracking | null> {
  try {
    const { data, error } = await supabase
      .from('counterfactual_tracking')
      .insert({
        journal_entry_id: input.journalEntryId,
        user_id: input.userId,
        price_at_decision: input.priceAtDecision,
        direction_if_traded: input.directionIfTraded,
        hypothetical_stop_loss: input.hypotheticalStopLoss,
        hypothetical_risk_amount: input.hypotheticalRiskAmount || DEFAULT_RISK_AMOUNT_USD,
        tracking_status: 'PENDING',
      })
      .select()
      .single();

    if (error) {
      console.error('[CounterfactualService] Error creating tracking:', error);
      return null;
    }

    return rowToCounterfactual(data as CounterfactualTrackingRow);
  } catch (err) {
    console.error('[CounterfactualService] Exception creating tracking:', err);
    return null;
  }
}

/**
 * Get counterfactual tracking for a specific journal entry
 */
export async function getCounterfactualForEntry(
  journalEntryId: string
): Promise<CounterfactualTracking | null> {
  try {
    const { data, error } = await supabase
      .from('counterfactual_tracking')
      .select('*')
      .eq('journal_entry_id', journalEntryId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No data found - not an error
        return null;
      }
      console.error('[CounterfactualService] Error fetching tracking:', error);
      return null;
    }

    return rowToCounterfactual(data as CounterfactualTrackingRow);
  } catch (err) {
    console.error('[CounterfactualService] Exception fetching tracking:', err);
    return null;
  }
}

/**
 * Update counterfactual tracking with new price data
 */
export async function updateCounterfactualPrice(
  id: string,
  priceData: {
    priceAfter1h?: number;
    priceAfter4h?: number;
    priceAfter24h?: number;
  }
): Promise<boolean> {
  try {
    const updateData: Record<string, unknown> = {};
    
    if (priceData.priceAfter1h !== undefined) {
      updateData.price_after_1h = priceData.priceAfter1h;
    }
    if (priceData.priceAfter4h !== undefined) {
      updateData.price_after_4h = priceData.priceAfter4h;
    }
    if (priceData.priceAfter24h !== undefined) {
      updateData.price_after_24h = priceData.priceAfter24h;
      updateData.tracking_status = 'COMPLETED';
      updateData.tracking_completed_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('counterfactual_tracking')
      .update(updateData)
      .eq('id', id);

    if (error) {
      console.error('[CounterfactualService] Error updating price:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('[CounterfactualService] Exception updating price:', err);
    return false;
  }
}

/**
 * Calculate and update capital preservation metrics
 */
export async function calculateCapitalPreserved(
  tracking: CounterfactualTracking,
  currentPrice: number
): Promise<CounterfactualTracking | null> {
  try {
    const { priceAtDecision, directionIfTraded, hypotheticalStopLoss, hypotheticalRiskAmount } = tracking;
    
    if (!directionIfTraded || !hypotheticalStopLoss) {
      // Cannot calculate without direction and stop loss
      return tracking;
    }

    // Calculate price movement
    const priceChange = currentPrice - priceAtDecision;
    const priceChangePercent = (priceChange / priceAtDecision) * 100;
    
    // Calculate adverse/favorable excursion based on direction
    let maxAdverseExcursion = 0;
    let maxFavorableExcursion = 0;
    
    if (directionIfTraded === 'LONG') {
      // For LONG: adverse = price down, favorable = price up
      maxAdverseExcursion = Math.min(0, priceChange);
      maxFavorableExcursion = Math.max(0, priceChange);
    } else {
      // For SHORT: adverse = price up, favorable = price down
      maxAdverseExcursion = Math.max(0, priceChange);
      maxFavorableExcursion = Math.min(0, priceChange);
    }
    
    // Calculate R-multiple
    const stopDistance = Math.abs(priceAtDecision - hypotheticalStopLoss);
    const riskPerR = hypotheticalRiskAmount || DEFAULT_RISK_AMOUNT_USD;
    
    let capitalPreservedR = 0;
    
    if (directionIfTraded === 'LONG') {
      // For LONG: if price went down, we preserved capital
      if (priceChange < 0) {
        capitalPreservedR = Math.abs(priceChange) / stopDistance;
      } else {
        // Price went up - we "missed" this opportunity (negative preserved)
        capitalPreservedR = -(priceChange / stopDistance);
      }
    } else {
      // For SHORT: if price went up, we preserved capital
      if (priceChange > 0) {
        capitalPreservedR = priceChange / stopDistance;
      } else {
        // Price went down - we "missed" this opportunity
        capitalPreservedR = -(Math.abs(priceChange) / stopDistance);
      }
    }
    
    // Cap at reasonable values
    capitalPreservedR = Math.max(-5, Math.min(5, capitalPreservedR));
    
    const capitalPreservedUsd = capitalPreservedR * riskPerR;

    // Update in database
    const { data, error } = await supabase
      .from('counterfactual_tracking')
      .update({
        max_adverse_excursion: Math.abs(maxAdverseExcursion),
        max_favorable_excursion: Math.abs(maxFavorableExcursion),
        capital_preserved_r: parseFloat(capitalPreservedR.toFixed(2)),
        capital_preserved_usd: parseFloat(capitalPreservedUsd.toFixed(2)),
      })
      .eq('id', tracking.id)
      .select()
      .single();

    if (error) {
      console.error('[CounterfactualService] Error calculating capital preserved:', error);
      return null;
    }

    return rowToCounterfactual(data as CounterfactualTrackingRow);
  } catch (err) {
    console.error('[CounterfactualService] Exception calculating capital preserved:', err);
    return null;
  }
}

/**
 * Get counterfactual stats for the current user
 */
export async function getCounterfactualStats(days?: number): Promise<CounterfactualStats | null> {
  try {
    const { data, error } = await supabase.rpc('get_counterfactual_stats', {
      p_days: days || null,
    });

    if (error) {
      console.error('[CounterfactualService] Error fetching stats:', error);
      return null;
    }

    if (!data || !data.success) {
      return null;
    }

    return {
      totalTracked: data.total_tracked || 0,
      completedTracking: data.completed_tracking || 0,
      totalCapitalPreservedR: data.total_capital_preserved_r || 0,
      totalCapitalPreservedUsd: data.total_capital_preserved_usd || 0,
      avgCapitalPreservedR: data.avg_capital_preserved_r,
      avgMaxAdverseExcursion: data.avg_max_adverse_excursion,
      decisionsThatSavedCapital: data.decisions_that_saved_capital || 0,
      decisionsThatMissedOpportunity: data.decisions_that_missed_opportunity || 0,
    };
  } catch (err) {
    console.error('[CounterfactualService] Exception fetching stats:', err);
    return null;
  }
}

/**
 * Get all pending counterfactual trackings (for background processing)
 */
export async function getPendingTrackings(): Promise<CounterfactualTracking[]> {
  try {
    const { data, error } = await supabase
      .from('counterfactual_tracking')
      .select('*')
      .in('tracking_status', ['PENDING', 'TRACKING'])
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[CounterfactualService] Error fetching pending trackings:', error);
      return [];
    }

    return (data || []).map((row) => rowToCounterfactual(row as CounterfactualTrackingRow));
  } catch (err) {
    console.error('[CounterfactualService] Exception fetching pending trackings:', err);
    return [];
  }
}

/**
 * Determine if a journal entry should have counterfactual tracking
 */
export function shouldTrackCounterfactual(executionPermission: string): boolean {
  return ['NO_TRADE', 'WAIT'].includes(executionPermission);
}

/**
 * Estimate hypothetical stop loss based on market type and price
 */
export function estimateStopLoss(
  price: number,
  direction: 'LONG' | 'SHORT' | null,
  marketType: string
): number | null {
  if (!direction || !price) return null;
  
  // Default ATR-based stop percentages by market
  const stopPercentages: Record<string, number> = {
    gold: 0.5, // 0.5% for gold
    crypto: 2.0, // 2% for crypto
    indices: 0.8, // 0.8% for indices
    forex: 0.3, // 0.3% for forex
  };
  
  const stopPercent = stopPercentages[marketType] || 1.0;
  const stopDistance = price * (stopPercent / 100);
  
  if (direction === 'LONG') {
    return price - stopDistance;
  } else {
    return price + stopDistance;
  }
}
